import { createSelector } from 'reselect';
import { CLIENT_STATE } from './DebtManagementChartReducer';

export const clientId = (state, props) => props.clientId;

export const debtManagementChartReducer = state => state.DebtManagementChartReducer;

export const debtManagemementChart = createSelector(
  clientId,
  debtManagementChartReducer,
  (currentClient, reducer) => reducer[currentClient] || CLIENT_STATE,
);

const isCurrentPlanSelector = (state, props) => props.isCurrentPlan;

export const isPrint = (state, props) =>
  props.isPrint || debtManagemementChart(state, props).isPrint;

export const hasLoaded = (state, props) =>
  props.loaded || debtManagemementChart(state, props).loaded;

export const isPdf = (state, props) => props.isPdf || debtManagemementChart(state, props).isPdf;

export const goalId = (state, props) => props.goalId || debtManagemementChart(state, props).goalId;
export const currentGoalId = (state, props) => debtManagemementChart(state, props).currentGoalId;
export const fpId = (state, props) => props.fpId || debtManagemementChart(state, props).fpId;

export const goalChartSettings = (state, props) =>
  debtManagemementChart(state, props).chartSettings;

export const goalChartData = (state, props) =>
  props.chartData || debtManagemementChart(state, props).data;

export const chartCategories = (state, props) => goalChartData(state, props).Categories;

export const chartYearsToPayOff = (state, props) => goalChartData(state, props).YearsToPayOff;

export const chartIsNeverPaidOff = (state, props) => goalChartData(state, props).IsNeverPaidOff;

export const currentSeries = (state, props) => goalChartData(state, props).Series.Current;

export const recommendedSeries = (state, props) => goalChartData(state, props).Series.Recommended;

export const hasChartData = (state, props) => chartCategories(state, props).length > 0;

export const dataIsOutdated = (state, props) => {
  const hasCurrentData = hasChartData(state, props);
  const hasGoalId = goalId(state, props).length >= 0;
  const sameGoalId = goalId(state, props) === currentGoalId(state, props);
  return hasGoalId && hasCurrentData && !sameGoalId;
};

export const chartTitle = (state, props) => props.chartTitle || goalChartData(state, props).title;

export function renderChartLabels(paidOffDate) {
  const labelId = 'debt-management-flag';

  if (this.group) {
    this.group.destroy();
  }

  this.group = this.renderer
    .g()
    .attr({
      zIndex: 10,
      id: labelId,
    })
    .add();

  const indexOfGoalAge = this.xAxis[0].categories.indexOf(paidOffDate);

  const currentGoalAgeLine = {
    x1: this.xAxis[0].toPixels(indexOfGoalAge),
    x2: this.xAxis[0].toPixels(indexOfGoalAge),
    y1: this.yAxis[0].toPixels(0) + 15,
    y2: 100,
  };

  // define the dashed line SVG
  const path = [
    'M',
    currentGoalAgeLine.x1,
    currentGoalAgeLine.y1,
    'L',
    currentGoalAgeLine.x2,
    currentGoalAgeLine.y2,
  ];

  // render the dashed line
  this.renderer
    .path(path)
    .attr({
      'stroke-width': 2,
      stroke: '#7ed321',
    })
    .add(this.group);

  // render the bottom circle
  this.renderer
    .rect(currentGoalAgeLine.x1 - 20, currentGoalAgeLine.y1 - 8, 40, 16, 3)
    .attr({
      fill: '#7ed321',
    })
    .add(this.group);

  // render the bottom date label
  this.renderer
    .text(paidOffDate, currentGoalAgeLine.x1, currentGoalAgeLine.y1 + 5)
    .attr({
      align: 'center',
    })
    .css({
      fontSize: '14px',
      fontWeight: '700',
      color: 'white',
      fontFamily: 'Open Sans, helvetica, sans-serif',
    })
    .add(this.group);

  // render the paid off message
  this.renderer
    .text('PAID OFF', currentGoalAgeLine.x1, currentGoalAgeLine.y1 + 30)
    .attr({
      align: 'center',
    })
    .css({
      fontSize: '12px',
      fontWeight: '700',
      color: '#383c42',
      fontFamily: 'Open Sans, helvetica, sans-serif',
    })
    .add(this.group);

  // render the goal flag
  this.renderer
    .text(
      '\uf024', // fa-caret-down
      currentGoalAgeLine.x1 - 2,
      currentGoalAgeLine.y2,
    )
    .css({
      fontFamily: '"Font Awesome 5 Pro"',
      fontSize: '16px',
      fontWeight: '900',
      color: '#7ed321',
    })

    .add(this.group);
}

export const goalChartPopulated = createSelector(
  recommendedSeries,
  currentSeries,
  chartYearsToPayOff,
  chartIsNeverPaidOff,
  chartCategories,
  goalChartSettings,
  isPrint,
  isCurrentPlanSelector,
  (
    recommendedData,
    currentData,
    paidOffDate,
    noPayoff,
    categories,
    chartData,
    isPdfPage,
    isCurrentPlan,
  ) => {
    const recommended = !isCurrentPlan
      ? recommendedData.reverse().map(item => ({
          ...item,
          color: 'transparent',
          opacity: 0,
        }))
      : [];

    const current = currentData.reverse().map(item => ({
      ...item,
      color: 'transparent',
      opacity: 0,
    }));

    function load() {
      const chartLabels = renderChartLabels.bind(this);
      chartLabels(paidOffDate);
    }

    const sumSeries = seriesList =>
      seriesList.reduce(
        (group, serie) =>
          serie.data.map((value, index) => {
            if (group.length === 0) {
              return value;
            }
            return value + group[index];
          }),
        [],
      );

    const recommendationList = !isCurrentPlan
      ? {
          data: sumSeries(recommendedData),
          title: 'Proposed',
          name: 'Proposed',
          color: '#8A65FF',
          animation: !isPdfPage,
          fillColor: 'transparent',
        }
      : {};

    const currentList = {
      data: sumSeries(currentData),
      title: 'Current',
      name: 'Current',
      color: '#4CBAFF',
      animation: !isPdfPage,
      isCurrentPlan,
      fillColor: 'rgba(247,251,255, 0.68)',
    };

    const series = [currentList, recommendationList, ...recommended, ...current];

    return {
      ...chartData,
      xAxis: {
        ...chartData.xAxis,
        categories,
      },
      series,
      chart: {
        ...chartData.chart,
        events: {
          ...chartData.chart.events,
          load: noPayoff ? undefined : load,
          redraw: noPayoff ? undefined : load,
        },
      },
    };
  },
);
